import { useState, useRef, useCallback, useLayoutEffect } from 'react';
import { useMediaQuery } from '@mui/material';
import { MOBILE_BREAKPOINT } from '../styles/ds';

// How many candidate items we render (but may not all end up visible) so the
// hook can discover the true number that fit by directly measuring them —
// comfortably more than any real screen could show without scrolling.
const PROBE_CAP = 100;
const MOBILE_BATCH_SIZE = 25;
const noopRef = () => {};

/**
 * On mobile, displays cumulative batches of 25 without measuring screen
 * height. On desktop, computes how many items fit in the space available
 * below a container's start position, reactive to resize — so a page's
 * item count is never a fixed number, it's always "however many actually
 * fit right now."
 *
 * This measures the REAL rendered position of each candidate item (via
 * getBoundingClientRect) and counts how many land fully on-screen, rather
 * than estimating from one sampled item's width/height (columns * rows
 * arithmetic), which is inherently approximate.
 *
 * To make this possible, the caller must render up to `renderCount`
 * candidate items into `containerRef` on every pass — including, briefly,
 * a much larger candidate batch while the hook is probing for how many
 * truly fit. That probing pass is never visible: React resolves it (and
 * the settle-back-down render that follows) within the same layout-effect
 * cycle the browser treats as pre-paint.
 *
 * Capacity is only ever LOWERED when the hook actually observes an item
 * that doesn't fit within the current candidate batch. Critically, it is
 * NEVER lowered just because "everything rendered happened to fit" when
 * the caller couldn't supply a full candidate batch to test with — e.g. a
 * partial last page with fewer remaining records than the true capacity
 * trivially "fits", but that is not evidence the true capacity is smaller;
 * treating it as such previously caused `fitCount` to ratchet down a
 * little on every page navigation (page 1 of 2 -> page 2 of 3 -> ...).
 *
 * Returns:
 *  - `fitCount`: the settled, stable count — use for pagination math
 *    (`perPage`); on mobile this is the batch size.
 *  - `startIndex`: the desktop page offset, or zero for mobile's cumulative list.
 *  - `renderCount`: how many items to actually slice into the container
 *    this pass (>= fitCount only during a brief, invisible probing pass).
 *  - `containerRef`: attach to the items' direct DOM parent.
 *  - `itemRef`: no-op, kept only so existing call sites that still pass it
 *    to an item don't break — measurement no longer samples a single item.
 */
export function useFitCount({ fallback = 12, bottomMargin = 24, page = 1 } = {}) {
  const isMobile = useMediaQuery(`(max-width:${MOBILE_BREAKPOINT}px)`);
  const containerRef = useRef(null);
  const lastWidthRef = useRef(null);
  const [fitCount, setFitCount] = useState(null);
  const [probing, setProbing] = useState(true);
  // What we most recently asked the caller to render (set at the end of
  // every render, read by the layout effect that follows it) — lets
  // recompute() tell "rendered fewer than asked because there wasn't
  // enough data" apart from "rendered fewer because they didn't fit".
  const requestedRef = useRef(PROBE_CAP);

  const recompute = useCallback(() => {
    if (isMobile) return;
    const container = containerRef.current;
    if (!container) return;
    const limit = window.innerHeight - bottomMargin;
    const children = Array.from(container.children);
    if (children.length === 0) return;

    let count = 0;
    for (const child of children) {
      if (child.getBoundingClientRect().bottom <= limit + 0.5) count++;
      else break; // items render in visual order, so the first miss ends the run
    }
    count = Math.max(1, count);

    const requested = requestedRef.current;
    // The caller rendered fewer candidates than we asked for — there
    // wasn't enough data left to test true capacity with (e.g. a partial
    // last page). An "everything fit" result from a pass like this proves
    // nothing about the real capacity: a handful of leftover records
    // fits trivially regardless of how large the real capacity is.
    const dataLimited = children.length < requested;
    // Some rendered candidate genuinely didn't fit — a trustworthy
    // boundary regardless of how much data was available to test.
    const foundRealBoundary = count < children.length;

    if (foundRealBoundary) {
      setFitCount(prev => (prev === count ? prev : count));
      setProbing(false);
      return;
    }

    // Nothing overflowed in this batch. That's not proof there's no more
    // room — we might simply be out of candidates (data, or PROBE_CAP) to
    // test with — but "at least `count` fit" is true regardless of why
    // the batch was this size, so it's always safe to use as a floor:
    // grow fitCount up to `count` when that's bigger than what's already
    // trusted. Never shrink based on this alone (that needs a real found
    // boundary, above) — e.g. a small dataset that never overflows the
    // screen must still be allowed to grow fitCount past the fallback, or
    // it stays splitting into pages even though everything fits on one.
    setFitCount(prev => (prev == null || count > prev ? count : prev));

    setProbing(prevProbing => {
      if (!prevProbing) return false;
      if (dataLimited) return false; // ran out of data, not room — nothing more to learn right now
      return children.length < PROBE_CAP; // ran out of candidates to test — ask for a bigger batch next pass
    });
  }, [bottomMargin, isMobile]);

  // Re-measure on every render (new data, new page, a fresh probing pass —
  // all show up here).
  useLayoutEffect(() => {
    recompute();
  });

  // Re-measure whenever the container or window resizes.
  useLayoutEffect(() => {
    if (isMobile) return;
    setProbing(true);
    const container = containerRef.current;
    const reprobe = () => setProbing(true);
    if (!container || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', reprobe);
      return () => window.removeEventListener('resize', reprobe);
    }
    lastWidthRef.current = container.getBoundingClientRect().width;
    // Only a WIDTH change (sidebar toggle, window resize, browser zoom) is
    // external evidence worth re-probing for. The container's HEIGHT is not
    // an independent signal — it's the direct, mechanical result of
    // fitCount itself (fitCount rows rendered = fitCount * rowHeight tall).
    // Reacting to height changes too creates a feedback loop: fitCount
    // settles -> container height changes to match -> that height change
    // fires the observer -> re-probe -> fitCount can settle on a slightly
    // different count due to transient layout (e.g. a scrollbar appearing
    // during the probe) -> container height changes again -> fires again —
    // ping-ponging the page count (e.g. "עמוד 1 מתוך 3" <-> "מתוך 2")
    // with no actual resize ever happening.
    const ro = new ResizeObserver(entries => {
      const width = entries[0]?.contentRect?.width;
      if (width == null || width === lastWidthRef.current) return;
      lastWidthRef.current = width;
      reprobe();
    });
    ro.observe(container);
    window.addEventListener('resize', reprobe);
    return () => { ro.disconnect(); window.removeEventListener('resize', reprobe); };
  }, [isMobile]);

  const renderCount = probing ? PROBE_CAP : (fitCount ?? fallback);
  requestedRef.current = renderCount;

  return {
    fitCount: isMobile ? MOBILE_BATCH_SIZE : (fitCount ?? fallback),
    startIndex: isMobile ? 0 : (page - 1) * (fitCount ?? fallback),
    renderCount: isMobile ? page * MOBILE_BATCH_SIZE : renderCount,
    containerRef,
    itemRef: noopRef,
  };
}
